import {DecimalPipe} from "@angular/common"
import {Component, ElementRef, effect, inject, input, OnInit, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {MatMenuModule} from "@angular/material/menu"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {Enums} from "@enums"
import {TextureRevisionState} from "@generated"
import {Labels} from "@labels"
import {
    ImageTextureNodeTextureFragment,
    ImageTextureNodeTextureGQL,
    ImageTextureNodeTextureRevisionFragment,
} from "@material-editor/components/widgets/texture-revision-widget/texture-revision-widget.generated"
import {TextureInfoService} from "@material-editor/services/texture-info.service"
import tippy from "tippy.js"
import {z} from "zod"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {MaterialNode, TextureFromRevision} from "@cm/material-nodes"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {SocketConnectionsService} from "@socket-editor/services/socket-connections.service"
import {PreviewSceneService} from "@material-editor/services/preview-scene.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-revision-widget",
    templateUrl: "./texture-revision-widget.component.html",
    styleUrls: ["./texture-revision-widget.component.scss"],
    imports: [InputContainerComponent, MatMenuModule, DecimalPipe, NumericInputComponent],
})
export class TextureRevisionWidgetComponent implements OnInit {
    public readonly $material = input.required<MaterialNode>({alias: "material"})
    public readonly $materialId = input.required<string>({alias: "materialId"})
    public readonly $materialRevisionId = input<string | undefined>(undefined, {alias: "materialRevisionId"})

    readonly $textureIcon = viewChild<ElementRef<HTMLElement>>("textureIcon")
    readonly $textureInfoTemplate = viewChild<ElementRef<HTMLElement>>("textureInfoTemplate")

    readonly $thumbnailUrl = signal<string | undefined>(undefined)
    readonly $texture = signal<ImageTextureNodeTextureFragment | null | undefined>(undefined)
    readonly $textureRevision = signal<ImageTextureNodeTextureRevisionFragment | null>(null)

    private readonly imageTextureNodeTexture = inject(ImageTextureNodeTextureGQL)
    readonly previewSceneService = inject(PreviewSceneService)

    private textureInfoService = inject(TextureInfoService)
    private connections = inject(SocketConnectionsService)

    private tippyInitialized = false

    constructor() {
        effect(() => {
            const textureIcon = this.$textureIcon()
            const textureInfoTemplate = this.$textureInfoTemplate()
            if (textureIcon?.nativeElement && textureInfoTemplate?.nativeElement) {
                this.setUpTextureInfo(textureIcon.nativeElement, textureInfoTemplate.nativeElement)
            }
        })
    }

    get node() {
        return this.$material() as TextureFromRevision
    }

    ngOnInit() {
        void this.loadTextureRevision()
    }

    private setUpTextureInfo(textureIcon: HTMLElement, textureInfoTemplate: HTMLElement) {
        if (this.tippyInitialized) {
            return
        }
        this.tippyInitialized = true
        textureInfoTemplate.style.display = "block"
        tippy(textureIcon, {
            content: textureInfoTemplate,
            allowHTML: true,
            placement: "right",
            arrow: false,
            offset: [0, 20],
            interactive: false,
            interactiveBorder: 30,
            trigger: "mouseenter",
            theme: "light",
            animation: "scale-subtle",
            delay: [200, 0],
            appendTo: (reference: Element) => (reference.closest(".cdk-overlay-popover") as HTMLElement) ?? document.body,
        })
    }

    async loadTextureRevision() {
        const textureRevisionLegacyId = this.node.parameters.settings.textureRevisionLegacyId
        if (!textureRevisionLegacyId) {
            return
        }
        const {materialEditorData} = await fetchThrowingErrors(this.imageTextureNodeTexture)({
            materialId: this.$materialId(),
            materialRevisionId: this.$materialRevisionId(),
            textureRevisionLegacyId,
        })
        const textureRevision = materialEditorData.revision?.textureRevision
        if (!textureRevision) {
            return
        }
        this.$textureRevision.set(textureRevision)
        this.$texture.set(textureRevision.texture)
        this.$thumbnailUrl.set(textureRevision.thumbnailUrl ?? undefined)

        const texture = this.$texture()
        if (!texture) {
            throw Error("Texture undefined.")
        }
        this.textureInfoService.setTextureSizeInfo({
            textureTypeLabel: Labels.TextureType.get(texture.type)?.label ?? "",
            width: textureRevision.width,
            height: textureRevision.height,
        })
    }

    ensureNumber(value: unknown): number {
        return z.number().safeParse(value).data ?? 0
    }

    setTextureRevision(textureRevisionLegacyId?: number | null) {
        if (textureRevisionLegacyId) {
            this.node.updateSetting("textureRevisionLegacyId", textureRevisionLegacyId)
            this.loadTextureRevision().then(() => {
                this.previewSceneService.$refresh.next()
            })
        }
    }

    protected readonly Enums = Enums
    protected readonly TextureRevisionState = TextureRevisionState
    protected readonly Labels = Labels
}
